import { graphql } from 'babel-plugin-relay/macro'
import React, { useRef } from 'react'
import { usePaginationFragment } from 'react-relay-offline'

import { CollectionsShelfItem } from './CollectionsShelfItem'
import type { CollectionsShelfList_Collection$key } from './__generated__/CollectionsShelfList_Collection.graphql'
import type { CollectionsShelfListPaginationQuery } from './__generated__/CollectionsShelfListPaginationQuery.graphql'
import { logger } from '../../../../../../logger'
import { SkeletonItem } from '../Skeleton'
import type { ProductCarouselPaginatedListHandle } from '../ProductCarouselPaginatedList'
import { ProductCarouselPaginatedList } from '../ProductCarouselPaginatedList'
import { CollectionsShelfEmptyState } from './CollectionsShelfEmptyState'

export type CollectionsShelfListProps = {
  itemsPerPage: number
  connection: CollectionsShelfList_Collection$key
}

export const CollectionsShelfList = ({
  itemsPerPage,
  connection,
}: CollectionsShelfListProps) => {
  const listRef = useRef<ProductCarouselPaginatedListHandle>(null)

  const { data, isLoadingNext, hasNext, loadNext } = usePaginationFragment<
    CollectionsShelfListPaginationQuery,
    CollectionsShelfList_Collection$key
  >(
    graphql`
      fragment CollectionsShelfList_Collection on Collection
      @argumentDefinitions(count: { type: "Int" }, after: { type: "String" })
      @refetchable(queryName: "CollectionsShelfListPaginationQuery") {
        intelligentSearchProducts(after: $after, first: $count)
          @connection(key: "CollectionsShelfList_intelligentSearchProducts") {
          edges {
            node {
              id
              ...CollectionsShelfItem_IntelligentSearchProductResult
            }
          }
          totalCount
        }
      }
    `,
    connection
  )

  const handleLoadNext = () => {
    if (!hasNext) return

    loadNext(itemsPerPage, {
      onComplete(error) {
        if (error) {
          return logger.error({
            workflowType: 'collections-shelf-items-load-more',
            error: error instanceof Error ? error : new Error(String(error)),
          })
        }
      },
    })
  }

  const loadMoreItems = () => {
    listRef.current?.goToNextPage()

    handleLoadNext()
  }

  if (!data.intelligentSearchProducts.edges.length) {
    return <CollectionsShelfEmptyState />
  }

  return (
    <ProductCarouselPaginatedList
      ref={listRef}
      totalCount={data.intelligentSearchProducts.totalCount}
      isLoading={isLoadingNext}
      data-testid="collections-shelf-list"
      itemsPerPage={itemsPerPage}
      onClickNext={loadMoreItems}
      onClickPrev={() => listRef.current?.goToPreviousPage()}
      onSwipeToRight={handleLoadNext}
    >
      {data.intelligentSearchProducts.edges.map((edge) => (
        <CollectionsShelfItem key={edge.node.id} connection={edge.node} />
      ))}
      {isLoadingNext &&
        Array.from({ length: itemsPerPage + 1 }).map((_, key) => (
          <SkeletonItem key={key} />
        ))}
    </ProductCarouselPaginatedList>
  )
}
